import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { fetchProductAsync, clearCurrentProduct } from '../../features/products/productsSlice';
import { makeOrderAsync } from '../../features/order/orderSlice';
import RetailerNavbar from '../../features/retailer/components/RetailerNavbar';
import { FaArrowLeft, FaShoppingCart, FaShoppingBag, FaVideo } from 'react-icons/fa';
import Footer from '../../features/components/Footer';

const RetailerProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentProduct, loading, error } = useSelector((state) => state.products);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [selectedMedia, setSelectedMedia] = useState('cover_image');

  useEffect(() => {
    dispatch(fetchProductAsync(productId));
    return () => {
      dispatch(clearCurrentProduct());
    };
  }, [dispatch, productId]);

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= 1 && value <= (currentProduct?.quantity || 1)) {
      setSelectedQuantity(value);
    }
  };

  const handleAddToCart = async () => {
    try {
      const orderData = {
        productId,
        quantity: selectedQuantity
      };
      
      await dispatch(makeOrderAsync({ 
        type: 'CART',
        orderData 
      })).unwrap();
      
      toast.success('Added to cart successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to add to cart');
    }
  };

  const handlePlaceOrder = async () => {
    try {
      const orderData = {
        productId,
        quantity: selectedQuantity
      };
      
      await dispatch(makeOrderAsync({ 
        type: 'ORDER',
        orderData 
      })).unwrap();
      
      toast.success('Order placed successfully!');
      navigate('/retailer/orders/pending');
    } catch (error) {
      toast.error(error.message || 'Failed to place order');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RetailerNavbar />
        <div className="pt-20 max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RetailerNavbar />
        <div className="pt-20 max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">{error}</h3>
              <button
                onClick={() => navigate('/retailer/dashboard')}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentProduct) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RetailerNavbar />
        <div className="pt-20 max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">Product not found</h3>
              <button
                onClick={() => navigate('/retailer/dashboard')}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const {
    name,
    weight,
    purity,
    making_charge: making,
    cover_image,
    image2,
    image3,
    video,
    quantity,
    registered_firm_name: registeredFirmName,
    description,
    category,
    owner_name: ownerName
  } = currentProduct;

  return (
    <div className="min-h-screen bg-gray-50">
      <RetailerNavbar />
      <div className="pt-20 max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Media Section */}
              <div className="space-y-4">
                {/* Main Media Display */}
                <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-200">
                  {selectedMedia === 'video' && video ? (
                    <video
                      src={video}
                      controls
                      className="h-full w-full object-cover object-center"
                    />
                  ) : (
                    <img
                      src={selectedMedia === 'cover_image' ? cover_image :
                          selectedMedia === 'image2' ? image2 :
                          selectedMedia === 'image3' ? image3 : cover_image}
                      alt={name}
                      className="h-full w-full object-cover object-center"
                    />
                  )}
                </div>

                {/* Thumbnail Gallery */}
                <div className="grid grid-cols-4 gap-2">
                  <div
                    className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                      selectedMedia === 'cover_image' ? 'ring-2 ring-yellow-500' : ''
                    }`}
                    onClick={() => setSelectedMedia('cover_image')}
                  >
                    <img
                      src={cover_image}
                      alt="Cover"
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                  
                  {image2 && (
                    <div
                      className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                        selectedMedia === 'image2' ? 'ring-2 ring-yellow-500' : ''
                      }`}
                      onClick={() => setSelectedMedia('image2')}
                    >
                      <img
                        src={image2}
                        alt="Image 2"
                        className="h-full w-full object-cover object-center"
                      />
                    </div>
                  )}

                  {image3 && (
                    <div
                      className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                        selectedMedia === 'image3' ? 'ring-2 ring-yellow-500' : ''
                      }`}
                      onClick={() => setSelectedMedia('image3')}
                    >
                      <img
                        src={image3}
                        alt="Image 3"
                        className="h-full w-full object-cover object-center"
                      />
                    </div>
                  )}

                  {video && (
                    <div
                      className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer relative ${
                        selectedMedia === 'video' ? 'ring-2 ring-yellow-500' : ''
                      }`}
                      onClick={() => setSelectedMedia('video')}
                    >
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                        <FaVideo className="text-white text-3xl" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Details Section */}
              <div className="space-y-6">
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <h2 className="text-2xl font-bold text-gray-900">{name}</h2>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {weight && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Weight</h3>
                          <p className="mt-1 text-sm text-gray-900">{weight} g</p>
                        </div>
                      )}
                      {purity && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Purity</h3>
                          <p className="mt-1 text-sm text-gray-900">{purity}K</p>
                        </div>
                      )}
                      {making && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Making Charge</h3>
                          <p className="mt-1 text-sm text-gray-900">{making}%</p>
                        </div>
                      )}
                      {quantity && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Available Quantity</h3>
                          <p className="mt-1 text-sm text-gray-900">{quantity}</p>
                        </div>
                      )}
                    </div>

                    {category && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Category</h3>
                        <p className="mt-1 text-sm text-gray-900 capitalize">{category}</p>
                      </div>
                    )}

                    {description && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Description</h3>
                        <p className="mt-1 text-sm text-gray-900">{description}</p>
                      </div>
                    )}

                    {(ownerName || registeredFirmName) && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Seller Details</h3>
                        <div className="mt-2 space-y-2">
                          {ownerName && (
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Name:</span> {ownerName}
                            </p>
                          )}
                          {registeredFirmName && (
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Firm:</span> {registeredFirmName}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Order Section */}
                  <div className="space-y-4 border-t pt-4">
                    <div className="flex items-center space-x-4">
                      <label htmlFor="quantity" className="text-sm font-medium text-gray-700">
                        Select Quantity:
                      </label>
                      <input
                        type="number"
                        id="quantity"
                        min="1"
                        max={quantity}
                        value={selectedQuantity}
                        onChange={handleQuantityChange}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <button
                        onClick={handleAddToCart}
                        className="flex-1 flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                      >
                        <FaShoppingCart className="mr-2" />
                        Add to Cart
                      </button>
                      <button
                        onClick={handlePlaceOrder}
                        className="flex-1 flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                      >
                        <FaShoppingBag className="mr-2" />
                        Order Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RetailerProductDetails;